import React, { useCallback, useMemo, useState } from "react";

import { faArrowUpRight, faSearch } from "@fortawesome/pro-regular-svg-icons";
import {
  createColumnHelper,
  DisplayColumnDef,
  Row,
} from "@tanstack/react-table";
import { twJoin } from "tailwind-merge";
import { useBoolean } from "usehooks-ts";

import { CollapsibleWidget } from "src/base-components/CollapsibleWidget";
import { Pill } from "src/base-components/Pill";
import { CustomPopover } from "src/base-components/Popover";
import {
  Cell,
  ColumnVisibilityDropdown,
  Header,
  TableComp,
} from "src/base-components/Table";
import { TruncatedIdPill } from "src/base-components/TruncatedIdPill";
import { SCHEMA_TYPE_ICONS } from "src/base-components/TypeIcons";
import { AccordionSkeleton } from "src/decisionsOverview/DecisionsOverview";
import { EmptyState } from "src/design-system/EmptyState";
import { Icon } from "src/design-system/Icon";
import { renderValue } from "src/entities/entityView/EntityDetailsSidePane";
import { EntityDetailsFloatingWindow } from "src/entities/entityView/EntityDetailsWindowPill";
import {
  findSchema,
  getOrderedProperties,
  getSchemaIcon,
} from "src/entities/entityView/utils";
import {
  Cardinality,
  EntitySchemaProperty,
  EntitySchemaResource,
  FlatEntityResource,
  SchemaRelationship,
  useEntitySchemas,
  useRelatedEntitiesByRelationship,
} from "src/entities/queries";
import {
  SensitiveField,
  SensitiveFieldValue,
} from "src/entitiesEventsCommon/SensitiveField";
import { RelationIcon } from "src/eventsCatalogue/SchemaEditor/RelationIcons";
import { RelationshipPopoverContent } from "src/eventsCatalogue/SchemaEditor/RelationshipPopoverContent";
import { Environment, useEnvironment } from "src/hooks/useEnvironment";
import { useWorkspaceContext } from "src/router/routerContextHooks";
import { EntityViewParams } from "src/router/urls";
import { useParamsDecode } from "src/utils/useParamsDecode";

type SetSelectedIdFn = React.Dispatch<React.SetStateAction<string | null>>;

type TableActions = {
  getSelectedId: () => string | null;
  setSelectedId: SetSelectedIdFn;
};

type BuildEntityColumnsOptions = {
  stickyIdColumn?: boolean;
};

const Title: React.FC<{
  isOutbound: boolean;
  property: string;
  entitySchema: EntitySchemaResource;
  relatedSchema: EntitySchemaResource;
}> = ({ property, entitySchema, relatedSchema, isOutbound }) => {
  const propertyDef = isOutbound
    ? entitySchema.properties?.[property]
    : relatedSchema.properties?.[property];
  const cardinality = propertyDef?._cardinality ?? Cardinality.ONE_TO_MANY;
  const { setTrue, setFalse, value: isOpen } = useBoolean(false);

  const button = (
    <div className="flex cursor-default items-center gap-x-2">
      <Pill size="sm" variant="gray">
        <Pill.Icon color="text-gray-400" icon={getSchemaIcon(entitySchema)} />
        <Pill.Text>
          <span className="capitalize">
            {entitySchema._display_name_singular}
          </span>
        </Pill.Text>
      </Pill>
      <h3 className="font-inter-semibold-13px">{propertyDef._display_name}</h3>
      <RelationIcon cardinality={cardinality} size="xs" />
      <Pill size="sm" variant="gray">
        <Pill.Icon color="text-gray-400" icon={getSchemaIcon(relatedSchema)} />
        <Pill.Text>
          <span className="capitalize">
            {cardinality === Cardinality.ONE
              ? relatedSchema._display_name_singular
              : relatedSchema._display_name_plural}
          </span>
        </Pill.Text>
      </Pill>
    </div>
  );

  return (
    <CustomPopover
      button={button}
      isOpen={isOpen}
      onMouseEnter={setTrue}
      onMouseLeave={setFalse}
    >
      <RelationshipPopoverContent
        cardinality={cardinality}
        pluralDisplayName={entitySchema._display_name_plural}
        propertyDisplayName={propertyDef._display_name}
        relatedSchemaId={relatedSchema._id}
        schemaId={entitySchema._id}
        singularDisplayName={entitySchema._display_name_singular}
      />
    </CustomPopover>
  );
};

const EntityTypeIcon: React.FC<{
  schema: EntitySchemaResource;
  propertyKey: string;
  property: EntitySchemaProperty;
}> = ({ schema, propertyKey, property }) => {
  if (property._type === "relation") {
    return (
      <div className="mr-0.5 shrink-0">
        <RelationIcon cardinality={property._cardinality} size="xs" />
      </div>
    );
  }
  const icon =
    schema._primary_property === propertyKey
      ? faArrowUpRight
      : (SCHEMA_TYPE_ICONS[property._type as keyof typeof SCHEMA_TYPE_ICONS] ??
        faArrowUpRight);
  return <Icon icon={icon} size="2xs" />;
};

export const buildEntityColumns = (
  schema: EntitySchemaResource,
  environment: Environment,
  options?: BuildEntityColumnsOptions,
): DisplayColumnDef<FlatEntityResource, unknown>[] => {
  const helper = createColumnHelper<FlatEntityResource>();
  const orderedFields = getOrderedProperties(schema).filter(
    (propertyKey) => propertyKey !== "id",
  );

  const idColumn = (() => {
    if (!("id" in schema.properties)) {
      return null;
    }

    const idProperty = schema.properties.id;

    return helper.accessor((row) => row.id, {
      id: "id",
      meta: {
        displayName: idProperty._display_name,
        ...(options?.stickyIdColumn && { sticky: "left" }),
      },
      enableHiding: false,
      header: (info) => (
        <Header info={info}>
          <div className="flex items-center gap-x-0.5">
            <Icon icon={SCHEMA_TYPE_ICONS["string"]} size="2xs" />
            <p className="whitespace-nowrap">{idProperty._display_name}</p>
          </div>
        </Header>
      ),
      size: 100,
      cell: (info) => {
        const value = info.getValue();
        const actions = info.table.options.meta?.actions as TableActions;
        return (
          <Cell className="w-24" info={info}>
            <TruncatedIdPill id={value} />
            <EntityDetailsFloatingWindow
              button={<></>}
              entityIcon={getSchemaIcon(schema)}
              environment={environment}
              id={value}
              isOpen={actions.getSelectedId() === value}
              name={value}
              schemaId={schema._id}
              onClose={() => actions.setSelectedId(null)}
            />
          </Cell>
        );
      },
    });
  })();

  const entityColumns = orderedFields.map((propertyKey) => {
    const propertyDef = schema.properties[propertyKey];

    return helper.accessor(propertyKey, {
      id: propertyKey,
      meta: {
        displayName: propertyDef._display_name,
      },
      header: (info) => (
        <Header info={info}>
          <div className="flex items-center gap-x-0.5">
            <EntityTypeIcon
              property={propertyDef}
              propertyKey={propertyKey}
              schema={schema}
            />
            <p className="whitespace-nowrap">{propertyDef._display_name}</p>
          </div>
        </Header>
      ),
      cell: (info) => {
        const isSensitive = schema.properties?.[propertyKey]?._sensitive;

        if (isSensitive) {
          return (
            <Cell info={info}>
              <SensitiveField
                displayJsonFormat={false}
                propertyName={propertyKey}
                value={info.getValue() as SensitiveFieldValue}
              />
            </Cell>
          );
        }

        return (
          <Cell info={info}>
            {renderValue({
              property: propertyDef,
              propertyValue: info.getValue(),
              isWindowPill: true,
              foreginKeyToPrimaryProperty: info.row.original._fk_pp,
              maxDisplayLines: 1,
              environment,
            })}
          </Cell>
        );
      },
      maxSize: 300,
      size: 300,
    });
  });

  const detailsColumn = helper.display({
    id: "properties.options",
    header: (info) => (
      <div className="flex justify-end pr-1.5">
        <ColumnVisibilityDropdown min={3} table={info.table} />
      </div>
    ),
    size: 54,
    cell: () => null,
  });

  return [idColumn, ...entityColumns, detailsColumn].filter(
    Boolean,
  ) as DisplayColumnDef<FlatEntityResource, unknown>[];
};

const RelatedEntitiesContent: React.FC<{
  relatedSchema: EntitySchemaResource;
  entitySchema: EntitySchemaResource;
  relationship: SchemaRelationship;
  entityId: string;
}> = ({ relatedSchema, entitySchema, relationship, entityId }) => {
  const { workspace } = useWorkspaceContext();
  const [env] = useEnvironment();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const columns = useMemo(
    () => buildEntityColumns(relatedSchema, env),
    [relatedSchema, env],
  );

  const {
    data: relatedEntitiesData,
    isLoading: isLoadingRelatedEntities,
    fetchNextPage,
  } = useRelatedEntitiesByRelationship({
    schema: entitySchema._id,
    entityId,
    env,
    baseUrl: workspace.base_url ?? "",
    relationship,
  });

  const rowPropsGetter = useCallback((row: Row<FlatEntityResource>) => {
    const actions = row.getAllCells()[0].getContext().table.options.meta
      ?.actions as TableActions;
    return {
      onClick: () => {
        actions.setSelectedId((prev) =>
          prev === row.original._id ? null : row.original._id,
        );
      },
      classNameOverrides: twJoin(
        row.original._id === actions.getSelectedId()
          ? "bg-gray-50"
          : "hover:bg-gray-50",
        "cursor-pointer",
      ),
    };
  }, []);

  const tableActions = useMemo(
    () => ({
      setSelectedId,
      getSelectedId: () => selectedId,
    }),
    [selectedId],
  );

  const entities = useMemo(() => {
    if (!relatedEntitiesData?.pages) return [];

    return relatedEntitiesData.pages.flatMap(
      (page) => page.entities,
    ) as FlatEntityResource[];
  }, [relatedEntitiesData?.pages]);

  if (!isLoadingRelatedEntities && entities.length === 0) {
    return (
      <EmptyState
        dataLoc="related-entities-empty-state"
        description={`No ${relatedSchema._display_name_plural?.toLowerCase() ?? "related entities"} found for ${entityId} entity for ${relationship.property_name} relationship`}
        headline="No related entities"
        icon={faSearch}
      />
    );
  }

  return (
    <TableComp
      actions={tableActions}
      columns={columns}
      data={entities}
      dataLoc="related-entities-table"
      isLoading={isLoadingRelatedEntities}
      rowClassName="group"
      rowPropsGetter={rowPropsGetter}
      variant="compact"
      onScrolledToTheBottom={fetchNextPage}
    />
  );
};

const EntityAccordionItem: React.FC<{
  fromSchema: EntitySchemaResource;
  targetSchema: EntitySchemaResource;
  entitySchema: EntitySchemaResource;
  property: string;
  relationship: SchemaRelationship;
  defaultOpen: boolean;
  entityId: string;
}> = ({
  defaultOpen,
  property,
  fromSchema,
  targetSchema,
  entitySchema,
  relationship,
  entityId,
}) => {
  const isOutbound = entitySchema === fromSchema;
  const relatedSchema = isOutbound ? targetSchema : fromSchema;

  return (
    <CollapsibleWidget
      dataLoc="related-entities"
      defaultOpen={defaultOpen}
      title={
        <Title
          entitySchema={entitySchema}
          isOutbound={isOutbound}
          property={property}
          relatedSchema={relatedSchema}
        />
      }
    >
      <div className="decideScrollbar overflow-hidden p-4">
        <RelatedEntitiesContent
          entityId={entityId}
          entitySchema={entitySchema}
          relationship={relationship}
          relatedSchema={relatedSchema}
        />
      </div>
    </CollapsibleWidget>
  );
};

export const RelatedEntities = () => {
  const { schema, id: entityId } = useParamsDecode<EntityViewParams>();
  const { workspace } = useWorkspaceContext();

  const { data: schemas, isLoading: isLoadingSchemas } = useEntitySchemas({
    baseUrl: workspace.base_url ?? "",
    options: { enabled: !!workspace.base_url },
  });

  const relationships = useMemo(() => {
    if (!schemas) return [];

    const currentSchema = findSchema(schema, schemas);
    if (!currentSchema) return [];

    const isRelationProperty = (property: EntitySchemaProperty): boolean =>
      property._type === "relation" && Boolean(property._rel_schema);

    const isInboundRelation = (
      property: EntitySchemaProperty,
      targetSchemaId: string,
    ): boolean =>
      isRelationProperty(property) &&
      property._rel_schema === targetSchemaId &&
      (!property._backref || property._backref === null);

    const createRelationship = (
      fromSchemaId: string,
      propertyName: string,
      targetSchemaId: string,
    ): SchemaRelationship => ({
      from_schema: fromSchemaId,
      property_name: propertyName,
      target_schema: targetSchemaId,
    });

    const outboundRelationships = Object.entries(currentSchema.properties ?? {})
      .filter(([_, property]) =>
        isRelationProperty(property as EntitySchemaProperty),
      )
      .map(([propertyName, property]) =>
        createRelationship(
          currentSchema._id,
          propertyName,
          (property as EntitySchemaProperty)._rel_schema!,
        ),
      );

    const inboundRelationships = schemas.entities
      .filter((schema) => schema._id !== currentSchema._id)
      .flatMap((schema) =>
        Object.entries(schema.properties ?? {})
          .filter(([_, property]) =>
            isInboundRelation(
              property as EntitySchemaProperty,
              currentSchema._id,
            ),
          )
          .map(([propertyName]) =>
            createRelationship(schema._id, propertyName, currentSchema._id),
          ),
      );

    return [...outboundRelationships, ...inboundRelationships];
  }, [schemas, schema]);

  if (isLoadingSchemas) {
    return (
      <div className="p-5">
        <AccordionSkeleton />
      </div>
    );
  }

  if (!relationships?.length) {
    return null;
  }

  return (
    <div className="flex flex-col gap-y-3 p-5 pb-6">
      {relationships.map((relationship, index) => {
        const fromSchema = findSchema(relationship.from_schema, schemas);
        const targetSchema = findSchema(relationship.target_schema, schemas);
        const entitySchema = findSchema(schema, schemas);

        if (!fromSchema || !targetSchema || !entitySchema) {
          return null;
        }

        return (
          <EntityAccordionItem
            key={relationship.property_name}
            defaultOpen={index === 0}
            entityId={entityId}
            entitySchema={entitySchema}
            fromSchema={fromSchema}
            property={relationship.property_name}
            relationship={relationship}
            targetSchema={targetSchema}
          />
        );
      })}
    </div>
  );
};
